import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw, Search, Sparkles, Wrench } from 'lucide-react';
import { platformApi, type Release, type Roadmap } from '../../services/platformApi';
import { formatDateTime } from '../../shared/dateTimeFormatting';
import { portalBuild } from '../../shared/platformBuild';
import { usePlatformIdentity } from './usePlatformIdentity';

function commitUrl(repositoryUrl: string | undefined, sha: string) {
  if (!repositoryUrl || !/^https:\/\/github\.com\/[A-Za-z0-9_-]+\/[A-Za-z0-9_.-]+\/?$/.test(repositoryUrl) || !/^[a-f0-9]{7,40}$/i.test(sha)) return null;
  const url = new URL(repositoryUrl);
  const repository = url.pathname.split('/')[2];
  if (url.username || url.password || url.search || url.hash || !repository || !/^\/[A-Za-z0-9_-]+\/[A-Za-z0-9_.-]+\/?$/.test(url.pathname) || repository === '.' || repository === '..') return null;
  return `${repositoryUrl.replace(/\/$/, '')}/commit/${sha}`;
}

export function ReleaseCard({ release, featuredOnly = false, admin = false, repositoryUrl = portalBuild.repositoryUrl }: { release: Release; featuredOnly?: boolean; admin?: boolean; repositoryUrl?: string }) {
  const entries = [...release.entries].filter(entry => !featuredOnly || entry.featured).sort((left, right) => left.sortOrder - right.sortOrder);
  const deployment = release.deployments.find(item => item.status === 'succeeded');
  return <article className="platform-release">
    <header><span className="platform-tag">{release.label}</span><h3>{release.title}</h3></header>
    {deployment && <div className="platform-versions">
      <div>Deployment {deployment.number} · {deployment.environment} · Deployed: {formatDateTime(deployment.deployedAt)}</div>
      {deployment.components.map(build => {
        const link = admin && build.commitSha ? commitUrl(repositoryUrl, build.commitSha) : null;
        return <div key={build.component}><strong>{build.component === 'portal' ? 'Portal' : 'Backend'}</strong> {build.version} · Build: {build.buildId}
          {admin && build.commitSha && <div>SHA: {link ? <a href={link} target="_blank" rel="noopener noreferrer">{build.commitSha}</a> : build.commitSha}</div>}
        </div>;
      })}
    </div>}
    <p className="platform-prose">{release.description}</p>
    {entries.map(entry => <div className="platform-entry" key={entry.id}>
      {entry.kind === 'fix' ? <Wrench size={18} aria-label="Fix" /> : <Sparkles size={18} aria-label="Feature" />}
      <div><h4>{entry.title}</h4><p className="platform-prose">{entry.description}</p><small>{entry.component} · {entry.functionality}</small></div>
    </div>)}
    {featuredOnly && !entries.length && <p>No featured entries in this release.</p>}
  </article>;
}

export function RoadmapCard({ item }: { item: Roadmap }) {
  return <article className="platform-release"><header><h3>{item.title}</h3><span className="platform-tag">{item.status}</span></header><p className="platform-prose">{item.description}</p><small>{item.targetWindow || 'Target not announced'} · {item.component} · {item.functionality}</small></article>;
}

export function PlatformVersions({ admin = false }: { admin?: boolean }) {
  const identity = usePlatformIdentity();
  return <PlatformVersionsContent key={JSON.stringify([identity, admin])} admin={admin} />;
}

function PlatformVersionsContent({ admin }: { admin: boolean }) {
  const [backend, setBackend] = useState<Awaited<ReturnType<typeof platformApi.version>> | null>(null);
  const [error, setError] = useState('');
  useEffect(() => { let live = true; platformApi.version().then(value => { if (live) setBackend(value); }).catch(reason => { if (live) setError(String(reason.message || reason)); }); return () => { live = false; }; }, []);
  const builds = backend ? [portalBuild, backend] : [portalBuild];
  return <div className="platform-versions">{builds.map(build => <div key={build.component}><strong>{build.component === 'portal' ? 'Portal' : 'Backend'}</strong> {build.version} {build.isLocal && <span className="platform-tag">Local build</span>}
    {admin && <><div>Build: {build.buildId}</div><div>Built: {formatDateTime(build.builtAt)}</div><div>SHA: {build.commitSha || 'Unavailable'}</div></>}
  </div>)}{backend && <div>Environment: {backend.environment}{backend.deploymentNumber ? ` · Deployment ${backend.deploymentNumber}` : ''}</div>}{error && <p role="status">Backend version unavailable: {error}</p>}</div>;
}

export function PlatformPages({ view }: { view: 'releases' | 'roadmap' }) {
  const identity = usePlatformIdentity();
  return <PlatformPagesContent key={JSON.stringify([identity, view])} view={view} />;
}

function PlatformPagesContent({ view }: { view: 'releases' | 'roadmap' }) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [skip, setSkip] = useState(0);
  const [retry, setRetry] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [releases, setReleases] = useState<Release[]>([]);
  const [roadmap, setRoadmap] = useState<Roadmap[]>([]);
  const [latest, setLatest] = useState<Release | null>(null);
  const [hasMore, setHasMore] = useState(false);
  useEffect(() => {
    let live = true;
    setLoading(true); setError('');
    const request = view === 'releases' ? platformApi.releases({ skip, take: 10, search: query }) : platformApi.roadmap({ skip, take: 10, search: query });
    request.then(data => {
      if (!live) return;
      setHasMore(data.hasMore);
      if ('latest' in data) { setLatest(data.latest); setReleases(data.items as Release[]); } else setRoadmap(data.items as Roadmap[]);
    }).catch(reason => { if (live) setError(String(reason.message || reason)); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [view, query, skip, retry]);
  useEffect(() => { setSkip(0); setSearch(''); setQuery(''); }, [view]);
  return <div className="platform-page">
    <PlatformVersions />
    <form className="platform-toolbar" onSubmit={event => { event.preventDefault(); setSkip(0); setQuery(search); }}>
      <label className="platform-search"><Search size={18} /><input aria-label="Search platform updates" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search" /></label><button type="submit">Search</button>
    </form>
    {loading ? <p role="status">Loading…</p> : error ? <div role="alert"><p>{error}</p><button onClick={() => setRetry(value => value + 1)}><RefreshCw size={16} />Retry</button></div> : <>
      {view === 'releases' ? <>
        <h3>Latest Release</h3>{latest ? <ReleaseCard release={latest} featuredOnly /> : <p>No deployed release is available for this environment.</p>}
        <h3>Release History</h3>{releases.map(release => <ReleaseCard key={release.id} release={release} />)}{!releases.length && <p>No releases match your search.</p>}
      </> : <>{roadmap.map(item => <RoadmapCard key={item.id} item={item} />)}{!roadmap.length && <p>No upcoming features have been published.</p>}</>}
      <div className="platform-toolbar"><button aria-label="Previous page" title="Previous page" disabled={!skip} onClick={() => setSkip(value => Math.max(0, value - 10))}><ChevronLeft size={18} /></button><span>Page {skip / 10 + 1}</span><button aria-label="Next page" title="Next page" disabled={!hasMore} onClick={() => setSkip(value => value + 10)}><ChevronRight size={18} /></button></div>
    </>}
  </div>;
}